import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Code, Server, Shield, Activity, Heart, Stethoscope, ShoppingBag, CreditCard, TrendingUp, Cloud, Zap, Users, HardHat, Book, PenTool, Scale, Truck, Factory, Megaphone, Film, Settings, Home, Briefcase, Plane, UserPlus, FileText, Gavel, FileCheck, Package, MapPin, Cog, Wrench, Target, BarChart, Camera, Mic, Sliders, Clipboard, Key, Building, Phone, Map, Ticket, GraduationCap, Ruler, Braces } from 'lucide-react';
import FadeIn from '../components/FadeIn';
import SEO from '../components/SEO';

const industriesData = {
    'technology': {
        title: "Offshore IT & Tech Staffing | Top 1% Filipino Developers",
        metaDesc: "Scale your dev team with elite Filipino talent. From Full-Stack Developers to QA Engineers, we provide AI-augmented tech staffing at 70% less cost.",
        hero: {
            title: "Scale Your Dev Team Without the Silicon Valley Price Tag",
            subtitle: "Access the top 1% of Filipino technical talent. Pre-vetted, AI-enabled, and ready to deploy in your time zone.",
            image: "https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "The Tech Talent Shortage is a Myth (If You Look Globally)",
                text: "Local hiring is broken. You're fighting with Google and Meta for the same pool of expensive developers. Meanwhile, thousands of senior-level engineers in the Philippines are looking for opportunities to build world-class software.\n\nWe specialize in finding 'Unicorn' talent—developers who have excellent English, deep technical expertise, and the ability to work autonomously. We don't just fill seats; we build extended engineering pods that integrate directly into your Agile/Scrum sprints."
            },
            {
                type: 'benefits',
                heading: "Why Offshore IT with Us?",
                items: [
                    { icon: <Code size={24} />, title: "Full-Stack Expertise", desc: "React, Node, Python, AWS. Our talent pool covers the modern tech stack." },
                    { icon: <Shield size={24} />, title: "IP Protection", desc: "Bank-grade security protocols. Your code lives on your servers, not ours." },
                    { icon: <Server size={24} />, title: "DevOps & QA", desc: "Don't just hire coders. Hire the infrastructure pros that keep your app up." },
                    { icon: <Cloud size={24} />, title: "AI-Augmented Coding", desc: "All our devs use Copilot/Cursor to write cleaner code 2x faster." }
                ]
            },
            {
                type: 'deepDive',
                heading: "Roles We Fill Instantly",
                sections: [
                    {
                        title: "Software Engineering",
                        content: "Frontend (React/Vue), Backend (Node/Python/Go), and Mobile (React Native/Flutter). We test for algorithmic thinking, not just syntax memorization."
                    },
                    {
                        title: "Data Science & AI",
                        content: "Hire Data Analysts to clean your records or AI Engineers to fine-tune your LLMs. The Philippines has a booming data science community ready to tackle complex problems."
                    },
                    {
                        title: "Technical Support (L2/L3)",
                        content: "Move your Tier 2 and Tier 3 support offshore. Our engineers can debug code, run SQL queries, and resolve tickets while your local team sleeps."
                    }
                ]
            },
            {
                type: 'cta',
                heading: "Build Your Engineering Pod",
                text: "Stop pausing your roadmap because you can't hire fast enough. Let's stand up your offshore dev team in 14 days.",
                button: "Hire Developers Now"
            }
        ]
    },
    'healthcare': {
        title: "Healthcare BPO & Medical Staffing | HIPAA Compliant",
        metaDesc: "Reduce administrative burnout with HIPAA-compliant medical virtual assistants. We support clinics, telehealth, and insurance providers.",
        hero: {
            title: "Focus on Patients, Not Paperwork",
            subtitle: "HIPAA-compliant Medical Virtual Assistants (MVAs) and Registered Nurses (RNs) to handle your admin, billing, and triage.",
            image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "The Cure for Administrative Burnout",
                text: "Doctors and nurses didn't go to med school to fill out forms. Yet, 40% of their time is spent on EHR data entry and insurance verifications.\n\nOur Healthcare BPO solutions provide you with clinically trained staff—often Registered Nurses (RNs) in the Philippines—who act as virtual scribes, intake coordinators, and billing specialists. Because they handle the admin, your local providers can see more patients and provide better care."
            },
            {
                type: 'benefits',
                heading: "Clinical Excellence, Offshore Costs",
                items: [
                    { icon: <Stethoscope size={24} />, title: "Registered Nurses", desc: "Hire Philippine RNs to do non-patient facing clinical work for $12-15/hr." },
                    { icon: <Shield size={24} />, title: "HIPAA Certified", desc: "Every staff member undergoes rigorous HIPAA compliance training and background checks." },
                    { icon: <Activity size={24} />, title: "Revenue Cycle", desc: " accelerated billing and collections. We fight denials so you get paid faster." },
                    { icon: <Heart size={24} />, title: "Patient Empathy", desc: "Filipino culture is world-renowned for care and empathy, improving patient satisfaction." }
                ]
            },
            {
                type: 'deepDive',
                heading: "Specialized Medical Roles",
                sections: [
                    {
                        title: "Medical Virtual Assistant (MVA)",
                        content: "Handles patient scheduling, prior authorizations, prescription refills, and referral coordination. They live inside your EHR system."
                    },
                    {
                        title: "Virtual Scribe",
                        content: "Connects via audio during patient visits to document SOAP notes in real-time. This saves physicians 2-3 hours of charting per day."
                    },
                    {
                        title: "Telehealth Triage",
                        content: "RNs who answer patient calls, assess symptoms using your protocols, and route them to the appropriate level of care."
                    }
                ]
            },
            {
                type: 'cta',
                heading: "Reclaim Your Clinical Time",
                text: "See 20% more patients without working longer hours. Integrate a Medical Virtual Assistant today.",
                button: "Hire Medical Staff"
            }
        ]
    },
    'retail': {
        title: "E-commerce & Retail BPO | 24/7 CS & Operations",
        metaDesc: "Scale your Shopify/Amazon store with 24/7 customer support and inventory management. Reduce returns and boost LTV with our e-commerce specialists.",
        hero: {
            title: "Scale Your Store, Not Your Stress",
            subtitle: "Stop answering support tickets at 2 AM. Build a 24/7 engine for Customer Experience (CX) and Backend Operations.",
            image: "https://images.unsplash.com/photo-1556742049-0cfed4f7a07d?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "The 'Always-On' Customer",
                text: "Your customers shop 24/7. If you're not there to answer their questions instantly, they bounce to a competitor. But hiring local support for night shifts is expensive and hard to manage.\n\nWe build dedicated 24/7 support teams for DTC brands and retailers. Our agents don't just 'resolve tickets'; they are trained in consultative selling, turning inquiries into upsells. They also handle the unglamorous backend work—order processing, refunds, and chargeback disputes—so you can focus on product and brand."
            },
            {
                type: 'benefits',
                heading: "E-commerce Efficiency",
                items: [
                    { icon: <ShoppingBag size={24} />, title: "Omnichannel Support", desc: "We manage Email, Live Chat, SMS, and Social Media DMs in a unified inbox." },
                    { icon: <TrendingUp size={24} />, title: "Revenue Recovery", desc: "Agents pro-actively reach out to abandoned carts and failed payments to save sales." },
                    { icon: <Shield size={24} />, title: "Fraud Prevention", desc: "Dedicated teams review high-risk orders to prevent chargebacks before they happen." },
                    { icon: <Zap size={24} />, title: "Peak Scalability", desc: "Ramp up your team size by 300% for Black Friday/Cyber Monday, then scale back down." }
                ]
            },
            {
                type: 'deepDive',
                heading: "Roles That Drive GMV",
                sections: [
                    {
                        title: "Customer Success Specialist",
                        content: "Far beyond a call center rep. They know your product catalog inside out, offering sizing advice and product recommendations that increase Average Order Value (AOV)."
                    },
                    {
                        title: "E-commerce Virtual Assistant",
                        content: "Handles inventory management, supplier coordination, and product listing optimization (SEO titles, image alt tags) on Shopify, Amazon, and Walmart."
                    },
                    {
                        title: "Influencer Manager",
                        content: "Scouts, contacts, and manages relationships with hundreds of micro-influencers to drive organic traffic and UGC (User Generated Content) for your brand."
                    }
                ]
            },
            {
                type: 'cta',
                heading: "Prepare for Q4 Now",
                text: "Don't wait until the holiday rush to fix your operations. Build your scalable support infrastructure today.",
                button: "Get E-commerce Staffing Plan"
            }
        ]
    },
    'finance': {
        title: "Financial Services BPO | Accounting & Fintech Support",
        metaDesc: "Secure, CPAs and financial analysts for accounting firms and fintechs. Save 70% on bookkeeping, tax prep, and financial analysis.",
        hero: {
            title: "Financial Excellence, Global Efficiency",
            subtitle: "Expand your margin by hiring CPA-qualified accountants and financial analysts in the Philippines.",
            image: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "The Talent Crunch in Accounting",
                text: "The US is facing a massive shortage of accountants (the 'CPA Crisis'). Firms are turning away work because they literally cannot find staff to do it.\n\nThe Philippines produces tens of thousands of CPA-qualified graduates every year who are fluent in US GAAP, IFRS, and Australian tax laws. They are proficient in Xero, QuickBooks, NetSuite, and Excel. We help accounting firms and fintechs build dedicated offshore teams to handle the grunt work, allowing local partners to focus on advisory and client relationships."
            },
            {
                type: 'benefits',
                heading: "Bank-Grade Security & Compliance",
                items: [
                    { icon: <Shield size={24} />, title: "Data Security", desc: "ISO 27001 compliant environments with disabled USB ports, clean desk policies, and VPNs." },
                    { icon: <CreditCard size={24} />, title: "Cost Efficiency", desc: "Hire a Senior Accountant for the cost of a local admin assistant." },
                    { icon: <Activity size={24} />, title: "Accuracy", desc: "Multi-tier review processes ensure books are closed with 99.9% accuracy." },
                    { icon: <Code size={24} />, title: "Fintech Support", desc: "KYC/AML analysts to process verification documents for crypto and fintech apps." }
                ]
            },
            {
                type: 'deepDive',
                heading: "Specialized Financial Roles",
                sections: [
                    {
                        title: "Staff Accountant / Bookkeeper",
                        content: "Manages AP/AR, bank reconciliations, payroll processing, and month-end closing procedures."
                    },
                    {
                        title: "Tax Preparer",
                        content: "Prepares individual (1040) and corporate (1120) tax returns for review by US CPAs. Familiar with CCH Axcess, Lacerte, and Drake."
                    },
                    {
                        title: "Financial Analyst",
                        content: "Builds financial models, tracks KPIs, and creates investor presentations for CFOs and Private Equity firms."
                    }
                ]
            },
            {
                type: 'cta',
                heading: "Solve the Capacity Crisis",
                text: "Stop turning away clients. Build a scalable accounting team that works while you sleep.",
                button: "Hire Accountants"
            }
        ]
    },
    'saas': {
        title: "SaaS Operations & Support | L1/L2 Technical Support",
        metaDesc: "Hyper-growth support for SaaS companies. From technical onboarding to churn reduction, we help you scale your ARR.",
        hero: {
            title: "Scale Your SaaS Without Bloating Your Burn Rate",
            subtitle: "Customer Success, Technical Support, and Sales Development Reps (SDRs) who understand software.",
            image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "Support is Your New Sales Channel",
                text: "In SaaS, Net Dollar Retention (NDR) is everything. If your users can't figure out your product, they churn. If they have to wait 24 hours for a reply, they churn.\n\nWe provide technical support agents who are actual power users. They don't just read scripts; they troubleshoot APIs, debug integrations, and walk users through complex workflows via Zoom. They are your first line of defense against churn and your best source of product feedback."
            },
            {
                type: 'benefits',
                heading: "Growth-Engine Operations",
                items: [
                    { icon: <Cloud size={24} />, title: "Technical Aptitude", desc: "We hire agents with CS degrees who understand APIs, webhooks, and JSON." },
                    { icon: <TrendingUp size={24} />, title: "Churn Reduction", desc: "Pro-active Customer Success Managers (CSMs) who monitor health scores and intervene." },
                    { icon: <Zap size={24} />, title: "24/7 Coverage", desc: "Support your global user base across all time zones." },
                    { icon: <Users size={24} />, title: "SDR/BDR Teams", desc: "Outbound sales teams to prospect, qualify, and book demos for your Account Executives." }
                ]
            },
            {
                type: 'deepDive',
                heading: "Roles for Hyper-Growth",
                sections: [
                    {
                        title: "Technical Support Engineer (L1/L2)",
                        content: "Handles technical tickets, replicates bugs, and escalates directly to your engineering team via Jira/Linear."
                    },
                    {
                        title: "Customer Onboarding Specialist",
                        content: "Guides new enterprise clients through setup, data migration, and training to ensure 'Time to Value' is minimized."
                    },
                    {
                        title: "Sales Development Rep (SDR)",
                        content: "High-volume prospectors who use Apollo/ZoomInfo to find leads and run cold email/call sequences to fill your pipeline."
                    }
                ]
            },
            {
                type: 'cta',
                heading: "Extend Your Runway",
                text: "Get the support team of a Series C company on a Seed stage budget.",
                button: "Build SaaS Support Team"
            }
        ]
    },
    'construction': {
        title: "Construction BPO | Remote Estimators & BIM",
        metaDesc: "Scale your construction firm with offshore estimators, quantity surveyors, and BIM modelers. Save 60% on pre-construction costs.",
        hero: {
            title: "Build More, Bid More",
            subtitle: "Win more projects with a dedicated team of offshore estimators and BIM specialists.",
            image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "Pre-Construction at Scale",
                text: "The bottleneck in construction isn't always on the job site; it's in the back office. Bidding takes time. takeoff takes time. Drafting takes time.\n\nWe provide civil engineers and architects in the Philippines who specialize in US/UK building codes. They handle the tedious pre-construction work—quantity takeoffs, estimation, and 3D modeling—so your local team can focus on project management and client relationships."
            },
            {
                type: 'benefits',
                heading: "Engineering Support",
                items: [
                    { icon: <HardHat size={24} />, title: "Estimating Teams", desc: "Dedicated teams using Bluebeam, PlanSwift, and RSMeans to crank out bids." },
                    { icon: <PenTool size={24} />, title: "CAD & BIM", desc: "Revit, AutoCAD, and Civil 3D experts for drafting and modeling." },
                    { icon: <Clipboard size={24} />, title: "Project Admin", desc: "Handle RFI logs, submittals, and change orders remotely." },
                    { icon: <Braces size={24} />, title: "24/7 Operations", desc: "Your offshore team preps the work while you sleep, ready for review by 8 AM." } // Replaced 'Clock' with 'Braces' as fallback or just remove icon if needed. Wait, Clock is not imported. I'll use simple icons.
                ]
            },
            {
                type: 'deepDive',
                heading: "Specialized Construction Roles",
                sections: [
                    { title: "Construction Estimator", content: "Prepares detailed cost estimates, material takeoffs, and bid proposals." },
                    { title: "BIM Modeler / CAD Drafter", content: "Creates 3D models and 2D shop drawings. Coordinates clash detection." },
                    { title: "Procurement Coordinator", content: "Sources materials, negotiates with vendors, and tracks delivery schedules." }
                ]
            },
            {
                type: 'cta',
                heading: "Win More Bids",
                text: "Don't let capacity limits stop you from bidding. Scale your estimating team today.",
                button: "Hire Estimators"
            }
        ]
    },
    'education': {
        title: "Education & EdTech Support",
        metaDesc: "Support for EdTech platforms and educational institutions. Tutors, grading assistants, and student success support.",
        hero: {
            title: "Empowering Global Education",
            subtitle: "Student success support, LMS administration, and remote tutoring teams.",
            image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "Scalable Learning Support",
                text: "EdTech is booming, but student support often lags behind. We provide educated, empathetic staff to support learners of all ages.\n\nFrom handling technical LMS issues to providing subject-matter tutoring in Math, Science, and English, our teams help you deliver a better learning experience at scale."
            },
            {
                type: 'benefits',
                heading: "EdTech Operations",
                items: [
                    { icon: <Book size={24} />, title: "Tutoring Services", desc: "Subject matter experts available for 1-on-1 or group coaching." },
                    { icon: <GraduationCap size={24} />, title: "Student Success", desc: "Pro-active outreach to at-risk students to improve retention." },
                    { icon: <Settings size={24} />, title: "LMS Admin", desc: "Managing Canvas, Blackboard, or Moodle courses and enrollments." },
                    { icon: <Users size={24} />, title: "Enrollment Sales", desc: "Admissions teams to guide prospective students through the funnel." }
                ]
            },
            {
                type: 'deepDive',
                heading: "Education Roles",
                sections: [
                    { title: "LMS Administrator", content: "Manages course uploads, user enrollments, and technical troubleshooting." },
                    { title: "Online Tutor", content: "Provides academic support and grading assistance." },
                    { title: "Admissions Counselor", content: "Handles inbound inquiries and guides applicants through enrollment." }
                ]
            },
            {
                type: 'cta',
                heading: "Scale Your EdTech",
                text: "Support more students without increasing tuition. Build your team.",
                button: "Get Education Staffing"
            }
        ]
    },
    'engineering': {
        title: "Engineering Services Outsourcing (ESO)",
        metaDesc: "Civil, Mechanical, and Electrical engineering support. CAD drafting and technical documentation.",
        hero: {
            title: "Engineering Excellence",
            subtitle: "Extend your engineering capabilities with specialized offshore technical talent.",
            image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "Technical Precision",
                text: "Finding local engineering talent is expensive and competitive. We connect you with licensed engineers in the Philippines who can handle the technical heavy lifting.\n\nWhether it's Civil, Structural, Mechanical, or Electrical, our teams integrate with your workflow to produce high-quality drawings, calculations, and reports."
            },
            {
                type: 'benefits',
                heading: "Engineering Support",
                items: [
                    { icon: <Ruler size={24} />, title: "Civil & Structural", desc: "Structural analysis, foundation design support, and drafting." },
                    { icon: <Zap size={24} />, title: "MEP Engineering", desc: "Mechanical, Electrical, and Plumbing design and coordination." },
                    { icon: <PenTool size={24} />, title: "2D/3D CAD", desc: "Migration of legacy drawings to CAD/BIM." },
                    { icon: <FileCheck size={24} />, title: "Compliance", desc: "Ensuring designs meet international standards (ISO, ANSI)." }
                ]
            },
            {
                type: 'deepDive',
                heading: "Engineering Roles",
                sections: [
                    { title: "Junior Engineer", content: "Supports senior staff with calculations, data analysis, and report generation." },
                    { title: "CAD Technician", content: "Drafting and detailing using AutoCAD, MicroStation, or SolidWorks." },
                    { title: "Technical Writer", content: "Creates operation manuals, specs, and technical documentation." }
                ]
            },
            {
                type: 'cta',
                heading: "Augment Your Engineers",
                text: "Let your senior engineers focus on design, not drafting.",
                button: "Hire Engineers"
            }
        ]
    },
    'hr': {
        title: "HR & Recruitment Process Outsourcing (RPO)",
        metaDesc: "Global recruitment and HR support. Source candidates, manage payroll, and handle employee relations.",
        hero: {
            title: "Global Talent Acquisition",
            subtitle: "Scale your recruiting and HR operations with dedicated offshore teams.",
            image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "Recruiting at Speed",
                text: "In a war for talent, speed is everything. Our Recruitment Process Outsourcing (RPO) teams act as an extension of your internal HR.\n\nThey source candidates, screen resumes, schedule interviews, and manage the onboarding process, allowing your HR leaders to focus on culture and strategy."
            },
            {
                type: 'benefits',
                heading: "HR Efficiency",
                items: [
                    { icon: <UserPlus size={24} />, title: "Sourcing", desc: "Finding passive candidates on LinkedIn Recruiter and job boards." },
                    { icon: <FileText size={24} />, title: "Payroll Admin", desc: "Processing global payroll and benefits administration." },
                    { icon: <Users size={24} />, title: "Employee Relations", desc: "Handling day-to-day HR inquiries and documentation." },
                    { icon: <CheckCircle size={24} />, title: "Onboarding", desc: "Managing background checks and equipment provisioning." }
                ]
            },
            {
                type: 'deepDive',
                heading: "HR Roles",
                sections: [
                    { title: "Technical Recruiter", content: "Specialized in sourcing IT and engineering talent." },
                    { title: "HR Generalist", content: "Handles employee records, leave management, and policy compliance." },
                    { title: "Payroll Specialist", content: "Ensures accurate and timely payroll processing." }
                ]
            },
            {
                type: 'cta',
                heading: "Build Your Dream Team",
                text: "Hire the people who hire the people. Scale your recruiting capacity.",
                button: "Get RPO Support"
            }
        ]
    },
    'legal': {
        title: "Legal Process Outsourcing (LPO)",
        metaDesc: "Paralegal and legal support services. Contract review, legal research, and transcription.",
        hero: {
            title: "High-Value Legal Support",
            subtitle: "Law firms and corporate counsel save 70% by offshoring legal processes.",
            image: "https://images.unsplash.com/photo-1589829085413-56de8ae18c73?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "Legal Efficiency",
                text: "Lawyers shouldn't bill $500/hour for document formatting. Legal Process Outsourcing (LPO) allows firms to delegate routine tasks to qualified legal professionals in the Philippines.\n\nOur teams support litigation, real estate, and corporate law, ensuring accuracy and confidentiality."
            },
            {
                type: 'benefits',
                heading: "LPO Advantages",
                items: [
                    { icon: <Scale size={24} />, title: "Legal Research", desc: "Conducting case law research and summarizing findings." },
                    { icon: <FileCheck size={24} />, title: "Contract Review", desc: "First-pass review of NDAs and standard agreements." },
                    { icon: <Gavel size={24} />, title: "Litigation Support", desc: "Document coding, discovery support, and trial prep." },
                    { icon: <FileText size={24} />, title: "Transcription", desc: "Converting depositions and court hearings to text." }
                ]
            },
            {
                type: 'deepDive',
                heading: "Legal Roles",
                sections: [
                    { title: "Paralegal", content: "Drafts pleadings, manages case files, and communicates with clients." },
                    { title: "Legal Secretary", content: "Manages calendars, files court documents, and handles correspondence." },
                    { title: "Contract Administrator", content: "Manages contract lifecycles and database updates." }
                ]
            },
            {
                type: 'cta',
                heading: "Optimize Your Practice",
                text: "Increase billable hours by delegating the admin work.",
                button: "Hire Legal Staff"
            }
        ]
    },
    'logistics': {
        title: "Logistics & Supply Chain BPO",
        metaDesc: "Dispatch, freight forwarding, and inventory management support. 24/7 logistics operations.",
        hero: {
            title: "Keep Your Supply Chain Moving",
            subtitle: "24/7 dispatch and logistics coordination to ensure on-time delivery.",
            image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "Logistics Never Sleeps",
                text: "In logistics, increased volume means increased noise. More calls, more emails, more tracking updates.\n\nWe build 'Control Tower' teams that monitor shipments, coordinate with drivers, and resolve issues in real-time. Whether you're a 3PL, a freight broker, or a vast trucking fleet, we keep your wheels turning."
            },
            {
                type: 'benefits',
                heading: "Supply Chain Support",
                items: [
                    { icon: <Truck size={24} />, title: "Dispatch", desc: "Coordinating loads and drivers 24/7." },
                    { icon: <Package size={24} />, title: "Freight Brokerage", desc: "Covering loads and negotiating rates with carriers." },
                    { icon: <MapPin size={24} />, title: "Track & Trace", desc: "Pro-active monitoring of shipment status." },
                    { icon: <Clipboard size={24} />, title: "Back Office", desc: "Bill of Lading (BOL) processing and invoicing." }
                ]
            },
            {
                type: 'deepDive',
                heading: "Logistics Roles",
                sections: [
                    { title: "Logistics Coordinator", content: "Manages end-to-end shipment lifecycle." },
                    { title: "Dispatcher", content: "Assigns drivers to routes and monitors performance." },
                    { title: "Import/Export Clerk", content: "Handles customs documentation and compliance." }
                ]
            },
            {
                type: 'cta',
                heading: "Streamline Logistics",
                text: "Stop missing loads. Build a 24/7 logistics ops team.",
                button: "Hire Logistics Team"
            }
        ]
    },
    'manufacturing': {
        title: "Manufacturing & Production Support",
        metaDesc: "Production planning, QA, and supply chain support for manufacturers.",
        hero: {
            title: "Lean Manufacturing Support",
            subtitle: "Back-office efficiency for the factory floor.",
            image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "Optimize Production",
                text: "Manufacturing isn't just about machines; it's about data. Inventory levels, production schedules, quality reports.\n\nWe support manufacturers by handling the data-heavy side of operations. From BOM (Bill of Materials) management to production planning, we ensure your factory runs on schedule."
            },
            {
                type: 'benefits',
                heading: "Factory Support",
                items: [
                    { icon: <Factory size={24} />, title: "Production Planning", desc: "Scheduling runs based on demand forecasts." },
                    { icon: <Cog size={24} />, title: "Inventory Mgmt", desc: "Tracking raw materials and finished goods." },
                    { icon: <Wrench size={24} />, title: "Maintenance Admin", desc: "Scheduling equipment maintenance and tracking parts." },
                    { icon: <FileCheck size={24} />, title: "QA Documentation", desc: "Logging quality checks and compliance reports." }
                ]
            },
            {
                type: 'deepDive',
                heading: "Manufacturing Roles",
                sections: [
                    { title: "Production Planner", content: "Balances demand with capacity to create efficient schedules." },
                    { title: "Supply Chain Analyst", content: "Optimizes procurement and inventory levels." },
                    { title: "BOM Specialist", content: "Maintains accurate Bill of Materials records." }
                ]
            },
            {
                type: 'cta',
                heading: "Run Leaner",
                text: "Cut overhead, not corners. Optimize your manufacturing ops.",
                button: "Manufacturing Support"
            }
        ]
    },
    'marketing': {
        title: "Marketing & Advertising BPO",
        metaDesc: "Creative design, SEO, and performance marketing teams. Scale your agency or internal team.",
        hero: {
            title: "Scale Your Creative Output",
            subtitle: "Graphic design, content writing, and digital marketing execution at scale.",
            image: "https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "Creative at Speed",
                text: "Content is king, but producing it is expensive. Agencies and brands struggle to keep up with the demand for social posts, blogs, and ad creatives.\n\nWe provide creative pods—designers, writers, and editors—who work in your time zone to deliver high-quality assets. Scale your production without burning out your local team."
            },
            {
                type: 'benefits',
                heading: "Marketing Ops",
                items: [
                    { icon: <Megaphone size={24} />, title: "Social Media", desc: "Scheduling posts, community management, and graphic design." },
                    { icon: <Target size={24} />, title: "PPC Management", desc: "Campaign setup, bid management, and reporting." },
                    { icon: <BarChart size={24} />, title: "SEO", desc: "On-page optimization, link building, and keyword research." },
                    { icon: <CheckCircle size={24} />, title: "Email Marketing", desc: "Campaign layout, segmentation, and deployment." }
                ]
            },
            {
                type: 'deepDive',
                heading: "Marketing Roles",
                sections: [
                    { title: "Graphic Designer", content: "Creates social assets, banners, and ebooks." },
                    { title: "Content Writer", content: "Writes SEO-optimized blog posts and web copy." },
                    { title: "Marketing Coordinator", content: "Keeps campaigns on track and manages calendars." }
                ]
            },
            {
                type: 'cta',
                heading: "Amplify Your Brand",
                text: "Get a full creative team for the price of one local hire.",
                button: "Hire Creatives"
            }
        ]
    },
    'media': {
        title: "Media & Entertainment Services",
        metaDesc: "Video editing, animation, and post-production support for creators and studios.",
        hero: {
            title: "Post-Production Powerhouse",
            subtitle: "Video editing, sound engineering, and visual effects.",
            image: "https://images.unsplash.com/photo-1574717432741-032911a56e3d?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "Content Production",
                text: "The creator economy runs on video. But editing is a bottleneck.\n\nOur post-production teams use Adobe Premiere, After Effects, and DaVinci Resolve to turn raw footage into polished content. Whether you're a YouTuber or a production house, we help you publish faster."
            },
            {
                type: 'benefits',
                heading: "Production Support",
                items: [
                    { icon: <Film size={24} />, title: "Video Editing", desc: "Cutting, color grading, and sound mixing." },
                    { icon: <Camera size={24} />, title: "Animation", desc: "2D/3D animation and motion graphics." },
                    { icon: <Mic size={24} />, title: "Audio Engineering", desc: "Podcast editing and sound design." },
                    { icon: <FileText size={24} />, title: "Subtitling", desc: "Transcription and closed captioning for accessibility." }
                ]
            },
            {
                type: 'deepDive',
                heading: "Media Roles",
                sections: [
                    { title: "Video Editor", content: "End-to-end post-production for social and long-form video." },
                    { title: "Motion Graphics Artist", content: "Creates intros, lower thirds, and explainers." },
                    { title: "Thumbnail Artist", content: "Designs high-CTR thumbnails for YouTube." }
                ]
            },
            {
                type: 'cta',
                heading: "Create More",
                text: "Stop editing till 3 AM. Outsource your post-production.",
                button: "Hire Editors"
            }
        ]
    },
    'operations': {
        title: "Operations & Process Support",
        metaDesc: "Back-office optimization. Data entry, executive assistants, and process management.",
        hero: {
            title: "Operational Excellence",
            subtitle: "Streamline your back office and free up your core team.",
            image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "The Backbone of Business",
                text: "Every business has grunt work. Data entry, scheduling, file management. It's necessary, but it doesn't drive revenue.\n\nWe provide the operational backbone to keep your business running smoothly. Our process associates handle the details so your leadership can focus on the big picture."
            },
            {
                type: 'benefits',
                heading: "Ops Support",
                items: [
                    { icon: <Settings size={24} />, title: "Data Entry", desc: "Fast, accurate data processing and cleansing." },
                    { icon: <Sliders size={24} />, title: "Executive Assistants", desc: "Calendar management, travel booking, and email triage." },
                    { icon: <Clipboard size={24} />, title: "Research", desc: "Market research and lead enrichment." },
                    { icon: <CheckCircle size={24} />, title: "Quality Assurance", desc: "Auditing calls and processes for compliance." }
                ]
            },
            {
                type: 'deepDive',
                heading: "Operations Roles",
                sections: [
                    { title: "Executive Assistant (EA)", content: "The right hand to your CEO. Specialized in time management." },
                    { title: "Data Entry Specialist", content: "High-speed typing and attention to detail." },
                    { title: "Process Associate", content: "Follows SOPs to execute routine business processes." }
                ]
            },
            {
                type: 'cta',
                heading: "Optimize Ops",
                text: "Get the admin work off your plate. Scale your operations.",
                button: "Hire Ops Team"
            }
        ]
    },
    'real-estate': {
        title: "Real Estate Virtual Assistants",
        metaDesc: "Support for realtors, property managers, and investors. ISAs, transaction coordinators, and admin.",
        hero: {
            title: "Scale Your Portfolio",
            subtitle: "Property management and real estate support to help you close more deals.",
            image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "Real Estate Efficiency",
                text: "Top producers don't handle their own paperwork. They focus on closing.\n\nWe provide specialized Real Estate Virtual Assistants (REVAs) who understand the industry. From cold calling leads (ISA) to managing transaction checklists, we help you sell more homes."
            },
            {
                type: 'benefits',
                heading: "REVA Support",
                items: [
                    { icon: <Home size={24} />, title: "Inside Sales (ISA)", desc: "Cold calling and qualifying leads for agents." },
                    { icon: <Key size={24} />, title: "Property Mgmt", desc: "Handling tenant inquiries and maintenance requests." },
                    { icon: <FileCheck size={24} />, title: "Transaction Coord", desc: "Managing the paperwork from contract to close." },
                    { icon: <Building size={24} />, title: "Marketing", desc: "Creating flyers, listing descriptions, and social posts." }
                ]
            },
            {
                type: 'deepDive',
                heading: "Real Estate Roles",
                sections: [
                    { title: "Inside Sales Agent (ISA)", content: "Prospects for new leads and sets appointments." },
                    { title: "Transaction Coordinator", content: "Ensures all documents are signed and deadlines met." },
                    { title: "Property Manager Assistant", content: "Coordinates repairs and communicates with tenants." }
                ]
            },
            {
                type: 'cta',
                heading: "Close More Deals",
                text: "Stop doing the admin. Focus on your clients.",
                button: "Hire REVA"
            }
        ]
    },
    'sales': {
        title: "Sales & Business Development",
        metaDesc: "Outbound sales teams, SDRs, and appointment setters. Fill your pipeline with qualified leads.",
        hero: {
            title: "Fill Your Pipeline",
            subtitle: "Dedicated Sales Development Reps (SDRs) to prospect, qualify, and book meetings.",
            image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "Outbound Growth",
                text: "Cold calling is hard. But it works. The problem is consistency.\n\nWe build dedicated outbound sales teams that do nothing but prospect. They use tools like Apollo, ZoomInfo, and Salesforce to find decision-makers and book demos for your closers."
            },
            {
                type: 'benefits',
                heading: "Sales Development",
                items: [
                    { icon: <Phone size={24} />, title: "Cold Calling", desc: "High-volume outbound calling to set appointments." },
                    { icon: <Target size={24} />, title: "Lead Generation", desc: "Building verified lists of ideal prospects." },
                    { icon: <Briefcase size={24} />, title: "Account Mgmt", desc: "Nurturing existing client relationships." },
                    { icon: <TrendingUp size={24} />, title: "Sales Ops", desc: "CRM hygiene and reporting." }
                ]
            },
            {
                type: 'deepDive',
                heading: "Sales Roles",
                sections: [
                    { title: "SDR / BDR", content: "Focuses 100% on generating new opportunities." },
                    { title: "Lead Researcher", content: "Finds contact info and enriches data." },
                    { title: "Account Manager", content: "Handles renewals and upsells for existing accounts." }
                ]
            },
            {
                type: 'cta',
                heading: "Grow Revenue",
                text: "Stop wondering where your next lead is coming from.",
                button: "Build Sales Team"
            }
        ]
    },
    'travel': {
        title: "Travel & Hospitality BPO",
        metaDesc: "24/7 travel support for agencies and hotels. Reservations, customer service, and concierge.",
        hero: {
            title: "World-Class Hospitality",
            subtitle: "24/7 support for travel agencies, airlines, and hotels.",
            image: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&q=80&w=1200"
        },
        content: [
            {
                type: 'intro',
                heading: "The Traveler's Companion",
                text: "Travel disrupts schedules. Flights get cancelled. Plans change. Your customers need support 24/7.\n\nWe provide travel support teams that handle reservations, re-booking, and concierge services. With excellent English and a service-first mindset, we ensure your travelers feel taken care of."
            },
            {
                type: 'benefits',
                heading: "Travel Ops",
                items: [
                    { icon: <Plane size={24} />, title: "Reservations", desc: "Booking flights, hotels, and tours." },
                    { icon: <Ticket size={24} />, title: "Ticketing", desc: "Handling changes, cancellations, and refunds." },
                    { icon: <Map size={24} />, title: "Concierge", desc: "Planning itineraries and making dining reservations." },
                    { icon: <Phone size={24} />, title: "24/7 Support", desc: "Emergency assistance for travelers." }
                ]
            },
            {
                type: 'deepDive',
                heading: "Hospitality Roles",
                sections: [
                    { title: "Travel Agent", content: "Certified agents familiar with GDS (Sabre, Amadeus)." },
                    { title: "Booking Coordinator", content: "Manages reservations and vendor confirmations." },
                    { title: "Guest Services", content: "Handles inquiries and complaints for hotels/rentals." }
                ]
            },
            {
                type: 'cta',
                heading: "Upgrade Your Service",
                text: "Deliver 5-star service around the clock.",
                button: "Hire Travel Staff"
            }
        ]
    }

};

const Industries = () => {
    const { industryId } = useParams();
    const industry = industriesData[industryId];

    if (!industryId) {
        return <Navigate to="/industries/technology" replace />;
    }

    if (!industry) {
        return <Navigate to="/404" replace />;
    }

    return (
        <div className="bg-white">
            <SEO
                title={industry.title}
                description={industry.metaDesc}
                pageKey={`industry_${industryId}`}
            />
            {/* Hero */}
            <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#20222D] overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src={industry.hero.image} alt={industry.hero.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-[#20222D] mix-blend-multiply"></div>
                </div>
                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <FadeIn>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                            {industry.hero.title}
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            {industry.hero.subtitle}
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                {industry.content.map((block, index) => {
                    switch (block.type) {
                        case 'intro':
                        case 'education':
                            return (
                                <FadeIn key={index} className="mb-16">
                                    <h2 className="text-3xl font-bold text-[#20222D] mb-6">{block.heading}</h2>
                                    <div className="prose prose-lg break-words text-gray-600 leading-relaxed whitespace-pre-line">
                                        {block.text}
                                    </div>
                                </FadeIn>
                            );
                        case 'benefits':
                            return (
                                <FadeIn key={index} className="mb-16 bg-gray-50 p-8 rounded-2xl border border-gray-100">
                                    <h2 className="text-3xl font-bold text-[#20222D] mb-8 text-center">{block.heading}</h2>
                                    <div className="grid md:grid-cols-2 gap-8">
                                        {block.items.map((item, i) => (
                                            <div key={i} className="flex items-start">
                                                <div className="text-[#A2CB52] bg-white p-3 rounded-lg shadow-sm mr-4">
                                                    {item.icon}
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-[#20222D] text-lg mb-2">{item.title}</h3>
                                                    <p className="text-gray-600 text-sm">{item.desc}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </FadeIn>
                            );
                        case 'deepDive':
                            return (
                                <FadeIn key={index} className="mb-16">
                                    <h2 className="text-3xl font-bold text-[#20222D] mb-8">{block.heading}</h2>
                                    <div className="space-y-8">
                                        {block.sections.map((sec, i) => (
                                            <div key={i} className="border-l-4 border-[#A2CB52] pl-6 py-2">
                                                <h3 className="text-xl font-bold text-[#20222D] mb-2">{sec.title}</h3>
                                                <p className="text-gray-600 leading-relaxed">{sec.content}</p>
                                            </div>
                                        ))}
                                    </div>
                                </FadeIn>
                            );
                        case 'cta':
                            return (
                                <FadeIn key={index} className="mt-20 text-center bg-[#20222D] rounded-3xl p-12 relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-[#A2CB52] blur-[100px] opacity-20 pointer-events-none"></div>
                                    <h2 className="text-3xl font-bold text-white mb-6">{block.heading}</h2>
                                    <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">{block.text}</p>
                                    <Link to="/contact" className="inline-flex items-center bg-[#A2CB52] text-[#20222D] px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#B5D575] transition-all transform hover:scale-105">
                                        {block.button || "Contact Us"}
                                        <ArrowRight className="ml-2" size={20} />
                                    </Link>
                                </FadeIn>
                            );
                        default:
                            return null;
                    }
                })}
            </div>
        </div>
    );
};

export default Industries;
